// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { lanacNormPath, lanacParsePath, lanacPathFor, useEffect, useRef } from "../00-boot.jsx";
// ═══ useRouter — le domaine ROUTEUR D'URL, d'un seul tenant ═════════════════
//  Contenu (déplacé VERBATIM depuis App() par codemod) : le drapeau, les 4 refs
//  d'état, la transition de page, l'application d'une URL, et les 3 effets qui
//  font vivre le routeur :
//    1. view/sel → URL (pushState + mémoire de scroll web)
//    2. popstate (retour/avance navigateur) → routerApply + restauration du scroll
//    3. init / lien direct → appliquer l'URL UNE fois, quand les données sont prêtes
//
//  Le routeur est INCONDITIONNEL depuis le 16/07/2026 : le kill-switch
//  `lanac:router` a été retiré (aucun code ne l'écrivait — console DevTools
//  uniquement, donc inatteignable par le support/l'ops ; et son chemin de repli
//  n'était jamais testé, donc pas un repli). Le vrai rollback le surpasse :
//  git revert + push → rebuild Pages, ~2 min, atomique, pour tous. Le scénario
//  e2e S12 (navigation ⇄ URL + bouton retour) couvre ce hook à chaque run.
//
//  `withPageTransition` vit ICI parce que routerApply en dépend, et il est
//  RETOURNÉ : App() l'utilise encore (go, openInlineDetail, openMarketDetail…).
//  `routerBack` reste dans App() : c'est un handler de JSX qui dépend de
//  `restorePrevFilters` (domaine « historique de filtres », non concerné ici).
//  C7 · T14 (16/09) : une fiche hors échantillon n'est plus un lien mort. Ports optionnels : `listingOf` (échantillon,
//  fiche ouverte, annonces résolues), `resolveDetail(id)` → Promise<annonce visible|null> (une lecture),
//  `listingsReady` (l'échantillon du compte est posé), `onDetailMissing()`. Absents ⇒ comportement d'avant.
/** @param {any} me @param {{isMobile,view,setView,sel,setSel,items,listingOf?,resolveDetail?,listingsReady?,onDetailMissing?}} ports */
function useRouter(me, ports){
  const {isMobile,view,setView,sel,setSel,items}=ports;
  const routerInitedRef=useRef(false);   // l'URL initiale a-t-elle été appliquée ?
  const routerResolvingRef=useRef(false); // C7 · T14 : lien direct hors échantillon — une seule résolution
  const routerApplyingRef=useRef(false); // changement d'état venant d'un popstate (ne pas ré-empiler)
  const routerPathRef=useRef(null);      // dernier chemin synchronisé
  const routerScrollRef=useRef({});      // mémoire de scroll WEB par chemin
  //  (29/10/2026) ORDINATEUR : la position d'une liste se note AU CLIC qui ouvre une fiche. L'effet [view,sel] ci-dessous la lisait APRÈS l'affichage —
  //   or l'ouverture remonte la page d'abord (scrollTopSoon, une image plus tôt) : il notait 0, et le retour retombait en haut de la liste (vu par l'e2e
  //   S57 joué sur l'ordinateur). Une position notée au clic n'est plus écrasée par l'effet (le téléphone a sa propre mémoire : listScrollRef).
  const routerScrollPrisRef=useRef(null);
  const noterDefilement=()=>{ if(isMobile||routerPathRef.current==null) return; routerScrollRef.current[routerPathRef.current]=window.scrollY||document.documentElement.scrollTop||0; routerScrollPrisRef.current=routerPathRef.current; };
  const withPageTransition=(dir,apply)=>{
    const reduce=window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if(!isMobile||reduce||typeof document.startViewTransition!=="function"){apply();return;}
    const root=document.documentElement;
    root.dataset.lncNav=dir;
    let vt=null;
    try{
      vt=document.startViewTransition(()=>{
        // Nettoie un transform résiduel de geste (swipe-back) : l'ANCIEN snapshot garde la position
        // déjà glissée (continuité parfaite du geste), la NOUVELLE page repart propre.
        const pw=document.querySelector(".lanac-page-wrap");
        if(pw){pw.style.transform="";pw.style.transition="";pw.style.boxShadow="";pw.style.willChange="";pw.style.opacity="";pw.style.borderRadius="";pw.style.transformOrigin="";pw.style.filter="";}
        ReactDOM.flushSync(apply);
      });
    }catch(e){ delete root.dataset.lncNav; apply(); return; }
    // ⚠️ Les TROIS promesses d'une ViewTransition doivent être traitées, pas seulement `finished`.
    //  Quand une transition est ESCAMOTÉE (navigation plus rapide que l'animation, onglet masqué,
    //  seconde transition qui démarre), le navigateur REJETTE `ready` — et personne ne l'écoutait :
    //  rejet non géré remonté à `window.onunhandledrejection` (00-observability) puis journalisé
    //  en base. MESURÉ en prod : `client_errors` = « Transition was aborted because of invalid
    //  state », 59 occurrences, du 17/07 au 02/09/2026 — l'erreur client N°1 de la plateforme.
    //  Bénin pour l'utilisateur (la navigation a bien eu lieu, `flushSync` s'est exécuté), mais ça
    //  brûlait de l'egress à chaque geste et ça noyait le journal où doit se voir un VRAI défaut.
    //  On ABSORBE sans rien changer au rendu : l'escamotage est un cas NORMAL, pas une panne.
    vt.ready.catch(()=>{});
    vt.updateCallbackDone.catch(()=>{});
    vt.finished.catch(()=>{}).then(()=>{ if(root.dataset.lncNav===dir) delete root.dataset.lncNav; });
  };
  const routerApply=(parsed,anim,resolved=null)=>{
    if(!parsed) return;
    routerApplyingRef.current=true;
    const apply=()=>{
      if(parsed.kind==="publish"){
        // /deposer OUVRE le depot par-dessus l'accueil, puis l'URL est NORMALISEE vers /
        // (le modal n'est pas une vue routee — la table des vues et le dock ne bougent pas,
        // et la garde retour C7 du modal reste seule maitresse de son historique).
        setSel(null); setView("home");
        if(ports.openPublish) ports.openPublish();
        try{ window.history.replaceState({lanacRoute:"/"},"","/"); }catch(e){}
        return;
      }
      if(parsed.kind==="detail"){
        const it=resolved||(items||[]).find(i=>String(i.id)===String(parsed.selId))||(ports.listingOf?ports.listingOf(parsed.selId):null);
        if(it){ setSel(it); setView("market"); }
        else if(ports.resolveDetail){
          // C7 · T14 : retour vers une fiche qui n'est plus en mémoire — une lecture, puis on l'ouvre si l'adresse n'a pas bougé.
          const chemin=lanacPathFor("market",parsed.selId);
          ports.resolveDetail(parsed.selId).then(full=>{
            if(full&&lanacNormPath(location.pathname)===chemin){ routerApplyingRef.current=true; setSel(full); setView("market"); requestAnimationFrame(()=>{ routerApplyingRef.current=false; }); }
          });
        }
      } else {
        let v=parsed.view;
        if(v==="messages"||v==="inbox") v=isMobile?"inbox":"messages"; // /messages → liste (mobile) ou fil (desktop)
        setSel(null); setView(v);
      }
    };
    // anim=true UNIQUEMENT depuis popstate (retour utilisateur) → transition « pop ». Init/lien direct : direct.
    if(anim) withPageTransition("pop",apply); else apply();
    requestAnimationFrame(()=>{ routerApplyingRef.current=false; });
  };
  useEffect(()=>{
    if(!routerInitedRef.current) return;
    //  (revue, M1) la position notée au clic vaut pour CE passage, et lui seul : le drapeau est consommé à chaque passage (même par les sorties
    //   anticipées ci-dessous) — resté levé, il aurait empêché de noter la position de la navigation suivante.
    const pris=routerScrollPrisRef.current; routerScrollPrisRef.current=null;
    const path=lanacPathFor(view, sel?sel.id:null);
    const cur=lanacNormPath(location.pathname);
    if(routerApplyingRef.current){ routerPathRef.current=path; return; } // venant d'un popstate
    if(path===cur){ routerPathRef.current=path; return; }
    if(!isMobile&&routerPathRef.current!=null&&pris!==routerPathRef.current){ routerScrollRef.current[routerPathRef.current]=window.scrollY||document.documentElement.scrollTop||0; }
    try{ window.history.pushState({lanacRoute:path},"",path); }catch(e){}
    routerPathRef.current=path;
  },[view,sel]);
  // (popstate) retour/avance navigateur → relire l'URL → restaurer l'écran + le scroll
  useEffect(()=>{
    try{ if("scrollRestoration" in history) history.scrollRestoration="manual"; }catch(e){} // on pilote le scroll
    const onPop=()=>{
      routerScrollPrisRef.current=null; // (revue, M1) un retour entre le clic et l'effet : la note du clic ne vaut plus
      const cur=lanacNormPath(location.pathname);
      //  Audit 23/09/2026 (lot client 2, coquille P2-8) : un retour qui NE CHANGE PAS d'adresse (l'entrée d'un fil ouvert sur
      //   mobile, celle de la garde du dépôt) n'a rien à ré-appliquer — son propriétaire la traite (Messenger, PublishModal).
      //   L'appliquer rejouait la transition « pop » de la page entière pour rien.
      if(cur===routerPathRef.current) return;
      routerApply(lanacParsePath(cur),true); // retour utilisateur → transition « pop »
      routerPathRef.current=cur;
      if(!isMobile){ const y=routerScrollRef.current[cur]||0; requestAnimationFrame(()=>requestAnimationFrame(()=>window.scrollTo(0,y))); }
    };
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[isMobile,items]);
  // (init / lien direct) au chargement : appliquer l'URL une seule fois, quand les données sont prêtes
  useEffect(()=>{
    if(routerInitedRef.current||!me) return;
    const cur=lanacNormPath(location.pathname);
    const parsed=lanacParsePath(cur);
    if(parsed&&parsed.kind==="detail"&&!(items||[]).some(i=>String(i.id)===String(parsed.selId))){
      // attendre les annonces — puis (C7 · T14) une annonce HORS échantillon se lit UNE fois : avant, le routeur attendait
      //  pour toujours (lien partagé qui n'ouvre rien, adresse figée pour toute la session). Absente ⇒ l'accueil, et c'est dit.
      if(!ports.listingsReady||!ports.resolveDetail||routerResolvingRef.current) return;
      routerResolvingRef.current=true;
      ports.resolveDetail(parsed.selId).then(full=>{
        if(routerInitedRef.current) return;
        routerInitedRef.current=true;
        routerPathRef.current=cur;
        if(full) routerApply(parsed,false,full);
        else if(ports.onDetailMissing) ports.onDetailMissing();
      });
      return;
    }
    routerInitedRef.current=true;
    routerPathRef.current=cur;
    if(parsed) routerApply(parsed); // sinon (chemin inconnu/auth) : on laisse l'app telle quelle
  },[me,items,ports.listingsReady]);
  return {routerInitedRef,routerApplyingRef,routerPathRef,routerScrollRef,routerApply,withPageTransition,noterDefilement};
}
export { useRouter };
